/**
 * Polymorphic text component that decouples DOM semantics from visual style.
 * Architectural Context: Pure UI atom. Use `as` for the HTML element, `variant` for appearance—
 * e.g. render an h1 with body styling to preserve heading semantics for a11y/SEO.
 *
 * @param as - HTML element to render (h1, p, span, etc). Defaults from variant when omitted.
 * @param variant - Visual style (h1–h6, body-lg/md/sm, caption)
 * @param color - Text color; defaults by variant (headings/body-lg → primary, others → secondary)
 * @default variant 'body-md'
 *
 * @example
 * <Typography as="h1" variant="body-lg">Looks like body, semantics of h1</Typography>
 * <Typography variant="h2" color="secondary">Styled heading</Typography>
 */

import { ComponentPropsWithoutRef, ElementType, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { cn } from '@/utils/cn';
import { colorStyles, variantStyles, weightStyles } from './Typography.styles';
import type { TypographyColor, TypographyProps, TypographyVariant } from './Typography.types';

const getDefaultColor = (variant: TypographyVariant): TypographyColor => {
  if (variant.startsWith('h') || variant === 'body-lg') {
    return 'primary';
  }
  return 'secondary';
};

const getDefaultAs = (
  variant: TypographyVariant,
  providedAs?: ElementType
): ElementType => {
  if (providedAs) return providedAs;
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant)) {
    return variant as ElementType;
  }
  return 'p';
};

export function Typography<T extends ElementType = 'p'>({
  as,
  variant = 'body-md',
  color,
  weight,
  className,
  children,
  ...props
}: TypographyProps<T> & ComponentPropsWithoutRef<T>) {
  const Component = getDefaultAs(variant, as);
  const variantClass = variantStyles[variant];
  const selectedColor = color ?? getDefaultColor(variant);
  const colorClass = colorStyles[selectedColor];
  const weightClass = weight ? weightStyles[weight] : undefined;
  const mergedVariantWeight = twMerge(variantClass, weightClass);

  const AnyComponent = Component as ElementType<{ className?: string; children?: ReactNode }>;
  return (
    <AnyComponent
      className={cn(mergedVariantWeight, colorClass, className)}
      {...(props as ComponentPropsWithoutRef<T>)}
    >
      {children}
    </AnyComponent>
  );
}
